'use client'

import { faEllipsisH } from '@document-crunch/pro-regular-svg-icons'

import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { Tooltip } from '@dc-shared-web/components/base'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dc-shared-web/components/shadcn/dropdown-menu'
import { Button } from '@dc-shared-web/components/shadcn/button'

export interface BaseMenuItem<T = string> {
  text: T
  id?: string
  hidden?: boolean
  badge?: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
}

export default function TableActionMenu<MenuItem extends BaseMenuItem>({
  items,
  onSelect,
  className,
  'data-testid': dataTestId,
  disabled = false,
}: {
  items: MenuItem[]
  onSelect: (item: MenuItem) => void
  className?: string
  'data-testid': string
  disabled?: boolean
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className} disabled={disabled}>
        <Button
          data-testid={`${dataTestId}-table-action-menu-trigger`}
          variant='ghost'
          size='icon'
          disabled={disabled}
        >
          <span className='tw-sr-only'>Actions</span>
          <Icon icon={faEllipsisH} color={IconColor.tertiary} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid='main-table-popover-content'
        className='tw-space-y-2'
        align='end'
      >
        {items
          .filter((item) => item.hidden !== true)
          .map((item, index) => (
            <Tooltip
              key={index}
              active={!!item.disabledTooltip}
              content={item.disabledTooltip || ''}
              withArrow
              side='left'
              className='tw-max-w-[200px]'
            >
              {/* wrapping div is required so that a tooltip can still be displayed even when the item is disabled */}
              <div>
                <DropdownMenuItem
                  data-testid={`${dataTestId}-table-action-menu-item`}
                  className='tw-cursor-pointer'
                  onClick={() => onSelect(item)}
                  disabled={disabled || item.disabled}
                >
                  <div className='tw-flex tw-gap-2 tw-w-full tw-items-center tw-justify-between'>
                    <span>{item.text}</span>
                    {item.badge && <span>{item.badge}</span>}
                  </div>
                </DropdownMenuItem>
              </div>
            </Tooltip>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
